export type Bet = {
  bettingType: string;
  marketName: string;
  eventName: string;
  runnerName: string;
  descriptionPrefix: string;
  eventId: string;
  betSize: string;
  betId: string;
  marketId: string;
  selectionId: number;
  handicap: number;
  priceSize: {
    price: number;
    size: number;
  };
  bspLiability: number;
  side: "BACK" | "LAY";
  status: string;
  persistenceType: string;
  orderType: string;
  placedDate: string;
  matchedDate?: string;
  averagePriceMatched: number;
  sizeMatched: number;
  sizeRemaining: number;
  sizeLapsed: number;
  sizeCancelled: number;
  sizeVoided: number;
  stakeMatched: number;
  stakeRemaining: number;
  regulatorCode: string;
  customerOrderRef: string;
  potentialLoss: string;
  potentialProfit: string;
  competitionName: string;
  eventTypeId: string;
  currentPnL?: number;
  providerId?: string;
  serviceId?: string;
  provider: MarketProvider;
  fOdds: number;
  dfOdds: number;
  topic?: string;
};

export enum MarketProvider {
  BF = "BF",
  SB = "SB",
}

export interface CalculationBet {
  marketGroupId: string; // An ID to group bet of the same market. For BF ODDS market => marketId, for SB Bookmaker market => topic, for BF LINE market => <marketId>-<selectionId>, for SB fancy market => <marketId>-<selectionId>
  marketId: string;
  marketName: string;
  selectionId: number;
  side: "BACK" | "LAY";
  potentialProfit: string | number;
  potentialLoss: string | number;
  betSize: string | number;
  betId: string;
  averagePriceMatched: number | string;
  fOdds: number;
  dfOdds: number;
  rangeOdd: number;
  bettingType: string;
  provider: MarketProvider;
  handicap: number | null;
  handicapValues?: number[];
  runnerName?: string;
  stakeMatched?: number;
  stakeRemaining?: number;
}

export interface PredictedBet {
  marketGroupId: string;
  marketId: string;
  marketName: string;
  selectionId: number;
  side: Side;
  bettingType: string;
  provider: MarketProvider;
  averagePriceMatched: string | number;
  fOdds?: number;
  dfOdds?: number;
  stake?: number;
  rangeOdd?: number;
  handicapValues?: number[];
  handicap: number;
  runnerName?: string;
  stakeMatched?: number;
  stakeRemaining?: number;
}

export type Side = "BACK" | "LAY";

export type Market = {
  marketId: string;
  marketName: string;
  marketStartTime: string;
  inPlay?: boolean;
  runners: Runner[];
  eventType: MarketEventType;
  competition: Competition | null;
  event: MarketEvent;
  totalMatched?: number;
  mc?: MarketUpdate | null;
  description?: Record<string, any>;
};

export type Runner = {
  selectionId: number;
  runnerName: string;
  handicap: number;
  sortPriority: number;
  metadata?: Record<string, any>;
  status?: string | null;
};

export type MarketEventType = {
  id: string;
  name: string;
};

export type MarketEvent = {
  id: string;
  name: string;
  countryCode: string | null;
  openDate: string | null;
  timezone: string | null;
  venue: string | null;
};

type Competition = {
  id: string;
  name: string;
};

export type MarketResponse<T> = {
  message: string;
  data: T;
};

export type MarketUpdate = {
  id: string;
  st: number;
  bd: number;
  c: boolean;
  ipl: boolean;
  nw: number;
  nr: number;
  nar: number;
  lmt: number;
  tm: number;
  cm: boolean;
  rv: boolean;
  ts: number;
  r: MarketUpdateRunner[];
  tv?: number;
};

export type MarketUpdateRunner = {
  id: number;
  h: number;
  st: number;
  lpt: number;
  af: number;
  tm: number;
  rd: string;
  ex: {
    abp: number[];
    abs: number[];
    alp: number[];
    als: number[];
  };
};

export type FancyMarket = {
  providerId: string;
  serviceId: string;
  marketId: string;
  eventId: string;
  eventName: string;
  bettingType: string;
  descriptionPrefix: string;
  eventType: {
    id: string;
    name: string;
  };
  competition: {
    id: string;
    name: string;
  };
  runners: FancyRunner[];
  topic: string;
  updatedAt: string;
};

export type FancyRunner = {
  selectionId: string;
  runnerName: string;
  priority: number;
  minBetSize: number;
  maxBetSize: number;
  priceYes: number;
  spreadYes: number;
  priceNo: number;
  spreadNo: number;
  inPlay: number;
  isActive: number;
  priceResult: null | string | number;
  betAllow: number;
  status: FancyRunnerStatus;
  autoSuspendTime: string;
  handicap: number;
  providerId: string;
};

export enum FancyRunnerStatus {
  ACTIVE = "ACTIVE",
  BALL_RUNNING = "BALL_RUNNING",
  CLOSED = "CLOSED",
  SUSPENDED = "SUSPENDED",
  REMOVED = "REMOVED",
}

export type BookmakerMarket = {
  providerId: string;
  serviceId: string;
  marketId: string;
  name: string;
  eventId: string;
  eventName: string;
  bettingType: string;
  betAllow: number;
  minBet: number;
  maxBet: number;
  isActive: number;
  type: string;
  status: BookmakerMarketStatus;
  maxProfit: number;
  betDelay: number;
  oddType: string;
  offPlayMaxBet: number;
  isOtherRateActive: number;
  descriptionPrefix: string;
  eventType: {
    id: string;
    name: string;
  };
  competition: {
    id: string;
    name: string;
  };
  runners: BookmakerRunner[];
  topic: string;
};

export enum BookmakerMarketStatus {
  OPEN = "OPEN",
  BALL_RUNNING = "BALL_RUNNING",
  CLOSED = "CLOSED",
  SUSPENDED = "SUSPENDED",
  REMOVED = "REMOVED",
}

export type BookmakerRunner = {
  selectionId: number;
  runnerName: string;
  backPrice: number;
  layPrice: number;
  backVolume: number;
  layVolume: number;
  handicap: number;
  sort: number;
  status: BookmakerRunnerStatus;
};

export enum BookmakerRunnerStatus {
  ACTIVE = "ACTIVE",
  LOSER = "LOSER",
  BALL_RUNNING = "BALL_RUNNING",
  CLOSED = "CLOSED",
  SUSPENDED = "SUSPENDED",
  REMOVED = "REMOVED",
  WINNER = "WINNER",
}

export interface CalculatedExposureDto {
  betId: string;
  marketId: string;
  selectionId: string;
  side: "back" | "lay";
  price: number;
  betSize: number;
  stakeMatched: number;
}
